import { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
    ChevronLeft, ChevronRight, Check, CloudOff, Cloud, Loader2, ArrowLeft,
    FileDown, ShieldCheck, Wifi, WifiOff, RefreshCw, Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import { studyFormsAPI } from '@/lib/api';
import { getSteps, STUDY_SCHEMAS } from '@/lib/studySchema';
import StudyField from '@/components/study/StudyField';
import {
    getLocalStudy, saveLocalStudy, markStudyClean, queuePendingFile, getPendingFilesForStudy,
    removePendingFile, queuePendingDelete, getAllPendingDeletes, removePendingDelete, isOnline,
    setValidatePending,
} from '@/lib/studyOffline';

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);

export default function StudyFormPage() {
    const { prospectId, studyId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [study, setStudy] = useState(null);
    const [formData, setFormData] = useState({});
    const [localFiles, setLocalFiles] = useState([]); // {clientFileId, category, name, mime, objectUrl, blob, uploading}
    const [step, setStep] = useState(0);
    const [online, setOnline] = useState(isOnline());
    const [saveState, setSaveState] = useState('saved'); // saved | saving | pending
    const [validating, setValidating] = useState(false);
    const [validateQueued, setValidateQueued] = useState(false);
    const saveTimer = useRef(null);
    const formDataRef = useRef({});
    const syncing = useRef(false);

    const readOnly = study ? study.can_edit === false : false;
    const variant = formData.form_variant || study?.form_type || 'cee_fiches';
    const STEPS = getSteps(variant);

    // ---------- Chargement ----------
    useEffect(() => {
        let cancelled = false;
        (async () => {
            const local = await getLocalStudy(studyId);
            if (!cancelled && local?.data) { setFormData(local.data); formDataRef.current = local.data; }
            if (!cancelled && local?.validatePending) setValidateQueued(true);
            try {
                const res = await studyFormsAPI.get(studyId);
                if (cancelled) return;
                const srv = res.data;
                setStudy(srv);
                // Source des champs : local non-synchronisé prioritaire, sinon serveur
                if (local?.dirty && local?.data) {
                    setFormData(local.data); formDataRef.current = local.data;
                } else {
                    setFormData(srv.data || {}); formDataRef.current = srv.data || {};
                    await saveLocalStudy({ id: studyId, prospectId, data: srv.data || {}, rev: srv.rev, updatedAt: srv.updated_at, dirty: false });
                }
            } catch (e) {
                if (!isOnline()) {
                    toast.message('Mode hors-ligne', { description: 'Vos données locales sont chargées. La synchro se fera au retour du réseau.' });
                } else if (!local) {
                    toast.error("Impossible de charger le relevé d'étude");
                }
            }
            // Fichiers en attente
            const pend = await getPendingFilesForStudy(studyId);
            if (!cancelled) {
                setLocalFiles(pend.map((p) => ({ ...p, objectUrl: URL.createObjectURL(p.blob), uploading: false })));
            }
            if (!cancelled) setLoading(false);
            flushSync();
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [studyId]);

    // ---------- Suivi connexion ----------
    useEffect(() => {
        const goOnline = () => { setOnline(true); flushSync(); };
        const goOffline = () => { setOnline(false); setSaveState('pending'); };
        window.addEventListener('online', goOnline);
        window.addEventListener('offline', goOffline);
        const iv = setInterval(() => { if (isOnline()) flushSync(); }, 15000);
        return () => { window.removeEventListener('online', goOnline); window.removeEventListener('offline', goOffline); clearInterval(iv); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [studyId]);

    // ---------- Sauvegarde champs (locale immédiate + serveur débouncée) ----------
    const persistLocal = useCallback(async (data) => {
        await saveLocalStudy({ id: studyId, prospectId, data, dirty: true, updatedAt: new Date().toISOString() });
    }, [studyId, prospectId]);

    const doServerSave = useCallback(async () => {
        if (!isOnline()) { setSaveState('pending'); return; }
        setSaveState('saving');
        try {
            const res = await studyFormsAPI.update(studyId, formDataRef.current);
            await markStudyClean(studyId, res.data.rev, res.data.updated_at);
            setSaveState('saved');
        } catch {
            setSaveState('pending');
        }
    }, [studyId]);

    const onFieldChange = useCallback((key, value) => {
        if (readOnly) return;
        const next = { ...formDataRef.current, [key]: value };
        formDataRef.current = next;
        setFormData(next);
        persistLocal(next);
        setSaveState('saving');
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(doServerSave, 800);
    }, [readOnly, persistLocal, doServerSave]);

    // ---------- Fichiers ----------
    const uploadPending = useCallback(async (entry) => {
        if (!isOnline()) return;
        setLocalFiles((prev) => prev.map((f) => f.clientFileId === entry.clientFileId ? { ...f, uploading: true } : f));
        try {
            const res = await studyFormsAPI.uploadFile(studyId, entry.category, entry.blob, entry.clientFileId);
            const ref = res.data.file;
            await removePendingFile(entry.clientFileId);
            setLocalFiles((prev) => prev.filter((f) => f.clientFileId !== entry.clientFileId));
            setStudy((prev) => prev ? { ...prev, files: [...(prev.files || []), ref] } : prev);
        } catch {
            setLocalFiles((prev) => prev.map((f) => f.clientFileId === entry.clientFileId ? { ...f, uploading: false } : f));
        }
    }, [studyId]);

    const onAddFiles = useCallback(async (category, files) => {
        if (readOnly || !files.length) return;
        for (const file of files) {
            const clientFileId = uid();
            const entry = { clientFileId, studyId, category, name: file.name || 'fichier', mime: file.type || 'application/octet-stream', blob: file };
            await queuePendingFile(entry);
            setLocalFiles((prev) => [...prev, { ...entry, objectUrl: URL.createObjectURL(file), uploading: false }]);
            uploadPending(entry);
        }
    }, [readOnly, studyId, uploadPending]);

    const onDeleteServerFile = useCallback(async (fileId) => {
        if (readOnly) return;
        setStudy((prev) => prev ? { ...prev, files: (prev.files || []).filter((f) => f.id !== fileId) } : prev);
        if (isOnline()) {
            try { await studyFormsAPI.deleteFile(studyId, fileId); } catch { await queuePendingDelete(studyId, fileId); }
        } else {
            await queuePendingDelete(studyId, fileId);
        }
    }, [readOnly, studyId]);

    const onDeleteLocalFile = useCallback(async (clientFileId) => {
        await removePendingFile(clientFileId);
        setLocalFiles((prev) => prev.filter((f) => f.clientFileId !== clientFileId));
    }, []);

    // ---------- Synchronisation globale ----------
    const flushSync = useCallback(async () => {
        if (syncing.current || !isOnline()) return;
        syncing.current = true;
        try {
            // 1. Suppressions en attente
            const deletes = await getAllPendingDeletes();
            for (const d of deletes.filter((x) => x.studyId === studyId)) {
                try { await studyFormsAPI.deleteFile(studyId, d.fileId); } catch { /* keep */ }
                await removePendingDelete(d.key);
            }
            // 2. Fichiers en attente
            const pend = await getPendingFilesForStudy(studyId);
            for (const p of pend) await uploadPending(p);
            // 3. Champs non synchronisés
            const local = await getLocalStudy(studyId);
            if (local?.dirty) await doServerSave();
            // 4. Validation en attente (programmée hors-ligne) → on valide automatiquement
            const localAfter = await getLocalStudy(studyId);
            if (localAfter?.validatePending) {
                try {
                    const res = await studyFormsAPI.validate(studyId);
                    setStudy(res.data);
                    await setValidatePending(studyId, false);
                    setValidateQueued(false);
                    toast.success('Relevé validé automatiquement ✅ — PDF généré.');
                } catch (e) {
                    if (e.response) {
                        // Erreur de données (ex. 422) : impossible de réparer seul → on informe l'utilisateur
                        await setValidatePending(studyId, false);
                        setValidateQueued(false);
                        toast.error(e.response?.data?.detail || 'La validation automatique a échoué : ouvrez le relevé et vérifiez les champs.');
                    }
                    // Sinon (perte réseau) : on garde en attente, nouvelle tentative au prochain flush
                }
            }
        } finally {
            syncing.current = false;
        }
    }, [studyId, uploadPending, doServerSave]);

    // ---------- Validation ----------
    const handleValidate = async () => {
        if (formDataRef.current.confirmation !== true) {
            toast.error('Veuillez cocher la confirmation finale avant de valider.');
            setStep(STEPS.length - 1);
            return;
        }
        // Hors-ligne → on programme la validation (elle se fera automatiquement au retour du réseau)
        if (!isOnline()) {
            await persistLocal(formDataRef.current);
            await setValidatePending(studyId, true);
            setValidateQueued(true);
            toast.message('Validation programmée ⏳', {
                description: 'Pas de réseau : la validation et le PDF se feront automatiquement dès la reconnexion. Vous pouvez fermer l\'application.',
            });
            return;
        }
        setValidating(true);
        try {
            await flushSync();
            await doServerSave();
            const res = await studyFormsAPI.validate(studyId);
            setStudy(res.data);
            await setValidatePending(studyId, false);
            setValidateQueued(false);
            // Toast détaillé sur l'envoi email (brief Jérémy 01/09/2026 —
            // permet de diagnostiquer sans aller dans les logs Emergent).
            const emailResult = res.data?._email_result;
            if (emailResult) {
                if (emailResult.quota_exceeded) {
                    toast.error(
                        `⛔ Quota email dépassé`,
                        {
                            description: (
                                "Le PDF est bien généré mais AUCUN email ne peut partir : " +
                                "le provider a atteint sa limite quotidienne. " +
                                "Le CRM rejouera automatiquement les envois manqués au redémarrage suivant."
                            ),
                            duration: 15000,
                        }
                    );
                } else if (emailResult.queued) {
                    toast.success(
                        `Relevé validé ✅ — Email en cours d'envoi via ${emailResult.provider || 'unknown'}`,
                        {
                            description: emailResult.provider === 'resend'
                                ? 'Envoi via Resend (delivery premium, 2-5 s)'
                                : `⚠️ Envoi via ${emailResult.provider} — vérifie tes secrets PROD`,
                            duration: 8000,
                        }
                    );
                } else if (emailResult.sent_ok) {
                    toast.success(
                        `Relevé validé ✅ — Email envoyé à ${emailResult.to_emails?.length || 0} destinataire(s)`,
                        { description: (emailResult.to_emails || []).join(', ') }
                    );
                } else {
                    toast.error(
                        `Relevé validé ⚠️ mais l'email n'est PAS parti`,
                        {
                            description: `Erreur : ${emailResult.error || 'inconnue'}`,
                            duration: 12000,
                        }
                    );
                }
            } else {
                toast.success('Relevé validé — PDF généré ✅');
            }
        } catch (e) {
            // FIX Jérémy 01/09/2026 : ne bascule en "hors-ligne" QUE si le navigateur
            // détecte vraiment un problème réseau (navigator.onLine=false). Avant,
            // toute erreur sans `e.response` (timeout, 502, CORS transitoire...)
            // partait en file d'attente offline → validation jamais transmise
            // → aucun email malgré une connexion normale.
            if (!e.response && !isOnline()) {
                // Vraiment hors-ligne → programme pour la reconnexion
                await persistLocal(formDataRef.current);
                await setValidatePending(studyId, true);
                setValidateQueued(true);
                toast.message('Validation programmée ⏳', {
                    description: 'Connexion perdue. La validation se fera automatiquement dès le retour du réseau.',
                });
            } else {
                // Vraie erreur serveur / réseau flaky → on informe explicitement
                const detail = e.response?.data?.detail || e.message || 'Erreur inconnue';
                const status = e.response?.status || 'timeout';
                toast.error(`Erreur lors de la validation`, {
                    description: `${detail} (statut : ${status}). Réessaie dans quelques secondes.`,
                    duration: 12000,
                });
            }
        } finally {
            setValidating(false);
        }
    };

    const openPdf = async () => {
        try {
            const u = await studyFormsAPI.pdfObjectUrl(studyId);
            const a = document.createElement('a');
            a.href = u;
            a.download = `releve-etude-${studyId}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(u), 4000);
        }
        catch { toast.error('PDF indisponible'); }
    };

    if (loading) {
        return <DashboardLayout><div className="space-y-4"><Skeleton className="h-12 w-1/2" /><Skeleton className="h-96 w-full" /></div></DashboardLayout>;
    }

    const current = STEPS[Math.min(step, STEPS.length - 1)];
    const fileCtx = {
        studyId,
        serverFiles: study?.files || [],
        localFiles,
        onAdd: onAddFiles,
        onDeleteServer: onDeleteServerFile,
        onDeleteLocal: onDeleteLocalFile,
    };
    const pendingCount = localFiles.length;

    return (
        <DashboardLayout>
            <div className="space-y-5 max-w-4xl mx-auto pb-24" data-testid="study-form-page">
                {/* Header */}
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3 min-w-0">
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/prospects/${prospectId}`)} data-testid="study-back-btn">
                            <ArrowLeft className="w-4 h-4 mr-1" /> Dossier
                        </Button>
                        <div className="min-w-0">
                            <h1 className="text-lg sm:text-xl font-bold text-slate-900 truncate">Relevé d'étude — {study?.prospect_label}</h1>
                            <p className="text-xs text-slate-500">{STUDY_SCHEMAS[variant]?.label || 'Relevé d\'étude'}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {/* Statut connexion */}
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${online ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`} data-testid="study-online-status">
                            {online ? <Wifi className="w-3.5 h-3.5" /> : <WifiOff className="w-3.5 h-3.5" />}
                            {online ? 'En ligne' : 'Hors-ligne'}
                        </span>
                        {/* Statut sauvegarde */}
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-600" data-testid="study-save-status">
                            {saveState === 'saving' && <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Enregistrement…</>}
                            {saveState === 'saved' && <><Cloud className="w-3.5 h-3.5 text-emerald-600" /> Enregistré</>}
                            {saveState === 'pending' && <><CloudOff className="w-3.5 h-3.5 text-amber-600" /> En attente{pendingCount ? ` (${pendingCount})` : ''}</>}
                        </span>
                    </div>
                </div>

                {study?.status === 'validated' && (
                    <Card className="border-emerald-200 bg-emerald-50/50">
                        <CardContent className="p-3 flex items-center justify-between gap-3 flex-wrap">
                            <span className="inline-flex items-center gap-2 text-sm text-emerald-800 font-medium"><ShieldCheck className="w-4 h-4" /> Relevé validé{study.validated_at ? ` le ${study.validated_at.slice(0, 10)}` : ''} — modifiable à tout moment.</span>
                            <Button size="sm" variant="outline" onClick={openPdf} className="gap-1.5" data-testid="study-open-pdf"><FileDown className="w-4 h-4" /> PDF</Button>
                        </CardContent>
                    </Card>
                )}

                {validateQueued && study?.status !== 'validated' && (
                    <Card className="border-amber-300 bg-amber-50/70" data-testid="study-validate-queued">
                        <CardContent className="p-3 flex items-center gap-2.5 text-sm text-amber-800">
                            <Clock className="w-4 h-4 shrink-0" />
                            <span><span className="font-semibold">Validation en attente de réseau.</span> Elle se fera automatiquement (avec le PDF) dès la reconnexion — vous pouvez fermer l'application en toute sécurité.</span>
                        </CardContent>
                    </Card>
                )}

                {readOnly && (
                    <div className="rounded-lg bg-slate-100 border border-slate-200 text-slate-600 text-sm p-3">
                        Vous consultez ce relevé en lecture seule. Seuls le créateur, le VP payeur et les administrateurs peuvent le modifier.
                    </div>
                )}

                {/* Stepper */}
                <div className="flex items-center gap-1 overflow-x-auto pb-1" data-testid="study-stepper">
                    {STEPS.map((s, i) => (
                        <button key={s.id} onClick={() => setStep(i)}
                            className={`flex-shrink-0 text-[11px] px-2.5 py-1.5 rounded-full transition-colors whitespace-nowrap ${i === step ? 'bg-indigo-600 text-white' : i < step ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                            data-testid={`study-step-tab-${s.id}`}>
                            {i + 1}. {s.title}
                        </button>
                    ))}
                </div>

                {/* Contenu de l'étape */}
                <Card data-testid={`study-step-${current.id}`}>
                    <CardContent className="p-5 sm:p-6 space-y-6">
                        <div className="flex items-center gap-2">
                            <span className="w-7 h-7 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-bold">{step + 1}</span>
                            <h2 className="text-base md:text-lg font-bold text-slate-900">{current.title}</h2>
                        </div>
                        {current.sections.map((sec, si) => {
                            if (sec.showIf && !sec.showIf(formData)) return null;
                            return (
                                <div key={si} className="space-y-3">
                                    {sec.title && <h3 className="text-sm font-semibold text-slate-700 border-b border-slate-100 pb-1">{sec.title}</h3>}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
                                        {sec.fields.map((field, fi) => (
                                            <StudyField
                                                key={field.key || field.category || `f-${fi}`}
                                                field={field}
                                                value={field.key ? formData[field.key] : undefined}
                                                onChange={onFieldChange}
                                                readOnly={readOnly}
                                                fileCtx={fileCtx}
                                            />
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </CardContent>
                </Card>

                {/* Navigation bas */}
                <div className="flex items-center justify-between gap-3">
                    <Button variant="outline" disabled={step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))} data-testid="study-prev-btn">
                        <ChevronLeft className="w-4 h-4 mr-1" /> Précédent
                    </Button>
                    {!online && (
                        <Button variant="ghost" size="sm" onClick={flushSync} className="gap-1 text-amber-700" data-testid="study-sync-btn">
                            <RefreshCw className="w-4 h-4" /> Synchroniser
                        </Button>
                    )}
                    {step < STEPS.length - 1 ? (
                        <Button onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))} className="bg-indigo-600 hover:bg-indigo-700" data-testid="study-next-btn">
                            Suivant <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                    ) : (
                        !readOnly && (
                            <Button onClick={handleValidate} disabled={validating || (validateQueued && !online)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="study-validate-btn">
                                {validating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                    : (validateQueued && !online) ? <Clock className="w-4 h-4 mr-1" />
                                        : <Check className="w-4 h-4 mr-1" />}
                                {(validateQueued && !online) ? 'Validation programmée ⏳'
                                    : study?.status === 'validated' ? 'Re-valider & régénérer le PDF'
                                        : !online ? 'Programmer la validation (hors-ligne)'
                                            : 'Valider & générer le PDF'}
                            </Button>
                        )
                    )}
                </div>
            </div>
        </DashboardLayout>
    );
}
